import { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { Link } from 'react-router-dom'
import { SmsAutoTransactionLogResponse, SmsAutoLogStatus } from '../../types'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'

type SmsAutoLogData = SmsAutoTransactionLogResponse

const statusConfig: Record<
	SmsAutoLogStatus,
	{
		label: string
		variant: 'default' | 'secondary' | 'destructive' | 'outline'
	}
> = {
	[SmsAutoLogStatus.UNMATCHED]: { label: 'รอดำเนินการ', variant: 'outline' },
	[SmsAutoLogStatus.PROCESSING]: {
		label: 'กำลังดำเนินการ',
		variant: 'secondary',
	},
	[SmsAutoLogStatus.REMATCHED]: { label: 'ดำเนินการแล้ว', variant: 'default' },
	[SmsAutoLogStatus.CANCELLED]: { label: 'ยกเลิก', variant: 'secondary' },
	[SmsAutoLogStatus.AUTO_MATCHED]: {
		label: 'จับคู่อัตโนมัติ',
		variant: 'default',
	},
}

export function createColumns(
	onRematch: (
		id: string,
		amount: number,
		accountNumber: string,
		message: string | null,
		gatewayMinDeposit?: number | null,
	) => void,
	onCancel: (id: string) => void,
): ColumnDef<SmsAutoLogData>[] {
	return [
		{
			accessorKey: 'createdAt',
			header: 'วันที่',
			cell: ({ row }) =>
				format(new Date(row.original.createdAt), 'dd/MM/yy HH:mm', {
					locale: th,
				}),
		},
		{
			accessorKey: 'amount',
			header: 'ยอดเงิน',
			cell: ({ row }) => `฿${row.original.amount.toLocaleString()}`,
		},
		{
			accessorKey: 'accountNumber',
			header: 'เลขบัญชี',
		},
		{
			accessorKey: 'status',
			header: 'สถานะ',
			cell: ({ row }) => {
				const status = row.original.status
				const config = statusConfig[status] ?? {
					label: status,
					variant: 'outline' as const,
				}
				return <Badge variant={config.variant}>{config.label}</Badge>
			},
		},
		{
			accessorKey: 'message',
			header: 'ข้อความ SMS',
			cell: ({ row }) => {
				const msg = row.original.message
				if (!msg) return <span className="text-muted-foreground">-</span>
				return (
					<span
						className="block max-w-[400px] whitespace-normal break-words text-xs"
						title={msg}
					>
						{msg}
					</span>
				)
			},
		},
		{
			accessorKey: 'paymentGateway',
			header: 'ธนาคารออโต้',
			cell: ({ row }) => row.original.paymentGateway?.bankProviderName || '-',
		},
		{
			id: 'amountChange',
			header: 'ยอดเปลี่ยน',
			cell: ({ row }) => {
				const item = row.original
				const lastLog = item.amountChangeLog?.[
					item.amountChangeLog.length - 1
				] as { from?: number; to?: number; reason?: string } | undefined
				const from = lastLog?.from ?? item.previousAmount ?? item.amount
				const to = lastLog?.to ?? item.amount
				if (!item.previousAmount && !item.amountChangeLog?.length)
					return <span className="text-xs text-muted-foreground">-</span>
				return (
					<span
						className="cursor-help text-xs text-amber-600"
						title={
							item.amountChangeLog
								?.map(
									(log) =>
										`${log.from?.toLocaleString() || '?'} → ${log.to?.toLocaleString() || '?'} [${log.reason || '-'}]`,
								)
								.join('\n') || ''
						}
					>
						{from !== to
							? `฿${from.toLocaleString()} → ฿${to.toLocaleString()}`
							: 'มีประวัติ'}
					</span>
				)
			},
		},
		{
			id: 'actions',
			header: 'จัดการ',
			cell: ({ row }) => {
				const item = row.original
				const isUnmatched = item.status === SmsAutoLogStatus.UNMATCHED
				const isProcessing = item.status === SmsAutoLogStatus.PROCESSING
				if (!isUnmatched && !isProcessing) {
					return (
						<span className="text-xs text-muted-foreground">
							{item.depositTransaction?.txId ? (
								<Link
									to={`/operator/transactions/deposits/${item.depositTransaction.txId}`}
									className="text-blue-600 hover:underline"
								>
									ดูรายการ
								</Link>
							) : (
								'-'
							)}
						</span>
					)
				}
				const disabled = false
				return (
					<div className="flex gap-2">
						<Button
							size="sm"
							variant="outline"
							className="h-7 px-2 text-xs"
							disabled={disabled}
							onClick={() =>
								onRematch(
									item.id,
									item.amount,
									item.accountNumber,
									item.message,
									item.paymentGateway?.minDepositAmount,
								)
							}
						>
							เติมเงิน
						</Button>
						<Button
							size="sm"
							variant="destructive"
							className="h-7 px-2 text-xs"
							disabled={disabled}
							onClick={() => onCancel(item.id)}
						>
							ยกเลิก
						</Button>
					</div>
				)
			},
		},
	]
}

export const columns: ColumnDef<SmsAutoLogData>[] = createColumns(
	() => {},
	() => {},
)
